import { useEffect, useState } from 'react'
import type { ShiftWindow } from '@/api/shiftDashboardApi'
import { clockTime, shiftProgress } from '@/lib/shiftProgress'
import { cn } from '@/lib/utils'

/**
 * The shift at a glance: start and end, where now sits between them, and how
 * long is left. The one bold element on the page — everything below it is
 * read against this clock. Ticks once a minute; no animation beyond that.
 */
export function ShiftBand({ window }: { window: ShiftWindow | undefined }) {
  const [now, setNow] = useState(() => new Date())
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60_000)
    return () => clearInterval(id)
  }, [])

  if (!window) return <div className='h-[4.5rem] animate-pulse rounded-xl bg-muted motion-reduce:animate-none' />

  const p = shiftProgress(window.from, window.to, now)
  const name = window.shift ? window.shift.name : 'Whole day'

  return (
    <section
      aria-label={`${name}, ${p.label || 'shift window'}`}
      className='rounded-xl border border-gray-200 bg-white shadow-sm px-5 py-3'
    >
      <div className='flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1'>
        <p className='text-sm text-muted-foreground'>
          <span className='text-base font-semibold text-foreground'>{name}</span>
          <span className='ml-2'>{window.label}</span>
        </p>
        <p
          className={cn(
            'text-sm font-semibold tabular-nums',
            p.phase === 'live' ? 'text-primary' : 'text-muted-foreground',
          )}
        >
          {p.label}
        </p>
      </div>

      <div className='mt-2.5 flex items-center gap-3 text-xs tabular-nums text-muted-foreground'>
        <span>{clockTime(window.from)}</span>
        <div
          className='relative h-2 flex-1 rounded-full bg-muted'
          role='progressbar'
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={p.percent}
          aria-valuetext={p.label}
        >
          <div
            className={cn('h-full rounded-full', p.phase === 'ended' ? 'bg-muted-foreground/40' : 'bg-primary')}
            style={{ width: `${p.percent}%` }}
          />
          {p.phase === 'live' && (
            <span
              aria-hidden
              className='absolute top-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-background bg-primary shadow'
              style={{ left: `${p.percent}%` }}
            />
          )}
        </div>
        <span>{clockTime(window.to)}</span>
      </div>
    </section>
  )
}
